/* Staten Tools engraving packages: single source of truth for package geometry,
   the illustrated pressable grid, hover/focus preview, and the front/back face
   package diagram that is exported with the order.
   The hidden native [data-eng-option] select remains the purchase state; this
   module only selects options that Liquid marked valid (available + exact price).
   API: window.stEngravingPackages = {PACKAGES, model, allowed, usable, selected,
        reconcile, render, renderDiagram, exportDiagram, fontLabel} */
(() => {
  'use strict';
  if (window.stEngravingPackages) return;
  const q = (r, s) => r.querySelector(s);
  const MODELS = ['6', '12', '18', '24', 'feed-3', 'feed-4', 'feed-5', 'feed-6'];
  const ANGLES = [0, 120, 240];
  const PACKAGES = {
    SINGLE: {name: 'Single', short: '1 mark on one wheel face', wheel: 1, frame: 0, per: 1,
      desc: 'One mark on the front wheel face.'},
    TRIPLE: {name: 'Triple', short: '3 marks at 120\u00b0 on one face', wheel: 1, frame: 0, per: 3,
      desc: 'Three marks spaced evenly at 120\u00b0 around the front wheel face. Back face left blank.'},
    BOTH: {name: 'Both faces', short: '1 mark on each wheel face', wheel: 2, frame: 0, per: 1,
      desc: 'One mark on the front wheel face and one on the back wheel face.'},
    'TRIPLE-BOTH': {name: 'Triple, both faces', short: '3 marks on each face, 6 total', wheel: 2, frame: 0, per: 3,
      desc: 'Three marks at 120\u00b0 on the front face and three at 120\u00b0 on the back face.'},
    'FRAME-BOTH': {name: 'Both faces + frame', short: '1 per wheel face + 1 frame side', wheel: 2, frame: 1, per: 1,
      desc: 'One mark on each wheel face plus one mark on one frame side. Three marks total.'},
    FRAME: {name: 'Frame side', short: '1 mark on one frame side', wheel: 0, frame: 1, per: 1,
      desc: 'One mark on one side of the sheave frame. No wheel marks.'}
  };
  for (const p of Object.values(PACKAGES)) p.total = p.wheel * p.per + p.frame;
  const HOOK = ['SINGLE', 'TRIPLE', 'BOTH', 'TRIPLE-BOTH', 'FRAME', 'FRAME-BOTH'];
  const FEED = ['FRAME'];
  // Top-level engraving surface -> detailed placement/repetition packages.
  const SURFACES = {
    wheel: {name: 'Wheel engraving', short: 'Marks on the wheel faces', codes: ['SINGLE', 'TRIPLE', 'BOTH', 'TRIPLE-BOTH']},
    frame: {name: 'Frame engraving', short: 'One mark on one frame side', codes: ['FRAME']},
    both: {name: 'Wheel + frame', short: 'Both wheel faces and one frame side', codes: ['FRAME-BOTH']}
  };
  const SURFACE_ORDER = ['wheel', 'frame', 'both'];
  const surfaceOf = code => SURFACE_ORDER.find(k => SURFACES[k].codes.includes(code)) || '';
  const surfaceCodes = (m, surface) => (SURFACES[surface]?.codes || []).filter(c => allowed(m).includes(c));
  const surfacesFor = m => SURFACE_ORDER.filter(k => surfaceCodes(m, k).length);
  const EXPECTED = {
    text: {SINGLE: 5000, TRIPLE: 7500, BOTH: 10000, 'TRIPLE-BOTH': 15000, 'FRAME-BOTH': 15000, FRAME: 5000},
    logo: {SINGLE: 10000, TRIPLE: 10000, BOTH: 10000, 'TRIPLE-BOTH': 15000, 'FRAME-BOTH': 15000, FRAME: 10000}
  };
  const FONTS = {sans: 'Sans \u2014 clean, bold', serif: 'Serif \u2014 classic', mono: 'Monospace \u2014 stencil-like'};
  const fontLabel = v => window.stEngravingFontLibrary?.[v]?.label || FONTS[v] || FONTS.sans;
  const states = new WeakMap();
  let uid = 0;

  const model = root => {
    const v = q(root, '[data-eng-sheave]')?.value ?? root.dataset.sheaveSize ?? '';
    return MODELS.includes(v) ? v : '';
  };
  function allowed(m) { return m.startsWith('feed-') ? FEED : m ? HOOK : []; }
  const select = root => q(root, '[data-eng-option]');
  function valid(o) {
    if (!o || !o.value || o.disabled || o.dataset.valid !== 'true') return false;
    const exp = EXPECTED[o.dataset.kind]?.[o.dataset.package];
    return !!exp && Number(o.dataset.price) === exp && Number(o.dataset.expected) === exp;
  }
  const usable = (root, o) => valid(o) && allowed(model(root)).includes(o.dataset.package);
  const find = (root, kind, pkg) => [...select(root).options].find(o => o.dataset.kind === kind && o.dataset.package === pkg && usable(root, o));
  function selected(root) {
    const o = select(root)?.selectedOptions[0];
    if (!usable(root, o)) return null;
    const p = PACKAGES[o.dataset.package];
    return {option: o, kind: o.dataset.kind, code: o.dataset.package, sku: o.dataset.sku, price: Number(o.dataset.price), ...p};
  }
  const money = (root, cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency',
        currency: root.dataset.currency || window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
    } catch { return `$${(cents / 100).toFixed(2)}`; }
  };
  const esc = s => String(s).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);
  const standalone = root => root.dataset.standalone === 'true';

  // Never leaves a disabled, sold-out, mispriced or model-incompatible option selected.
  function reconcile(root) {
    const sel = select(root);
    if (!sel) return false;
    const cur = sel.selectedOptions[0];
    if (cur && cur.dataset.none === 'true') return false;
    if (usable(root, cur)) return false;
    const st = state(root);
    const kind = cur?.dataset.kind || st.kind || 'text';
    const opts = [...sel.options].filter(o => usable(root, o));
    const pick = opts.find(o => o.dataset.kind === kind && o.dataset.package === cur?.dataset.package) ||
      opts.find(o => o.dataset.kind === kind && st.surface && surfaceOf(o.dataset.package) === st.surface) ||
      opts.find(o => o.dataset.kind === kind) || (standalone(root) ? opts[0] : null);
    const none = [...sel.options].find(o => o.dataset.none === 'true');
    const target = pick || none;
    if (target) target.selected = true; else sel.selectedIndex = -1;
    return true;
  }

  function state(root) {
    let st = states.get(root);
    if (!st) { st = {kind: '', surface: '', hover: '', bound: false, sig: ''}; states.set(root, st); }
    return st;
  }

  function relabel(root) {
    for (const o of select(root).options) {
      if (o.dataset.none === 'true' || o.dataset.labelled) continue;
      const p = PACKAGES[o.dataset.package];
      if (!p) continue;
      const reason = o.dataset.reason === 'soldout' ? ' (sold out)' : o.dataset.reason ? ' (unavailable)' : '';
      o.textContent = `${o.dataset.kind === 'logo' ? 'Logo' : 'Company text'} \u2014 ${p.name}: ${p.short} \u2014 ${money(root, Number(o.dataset.price))}${reason}`;
      o.dataset.labelled = 'true';
    }
  }

  // ---------- SVG illustrations ----------
  const pt = (cx, cy, r, deg) => [cx + r * Math.cos(deg * Math.PI / 180), cy + r * Math.sin(deg * Math.PI / 180)];
  function arcPath(cx, cy, r, mid, span) {
    const a = pt(cx, cy, r, mid + span / 2), b = pt(cx, cy, r, mid - span / 2);
    return `M${a[0].toFixed(2)} ${a[1].toFixed(2)}A${r} ${r} 0 0 0 ${b[0].toFixed(2)} ${b[1].toFixed(2)}`;
  }
  function face(cx, cy, r, marks, mid, label, big, sample) {
    let s = `<g class="st-pk__face${marks ? '' : ' is-blank'}"><circle cx="${cx}" cy="${cy}" r="${r}" class="st-pk__rim"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${(r * 0.74).toFixed(1)}" class="st-pk__web"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${(r * 0.24).toFixed(1)}" class="st-pk__hub"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${(r * 0.08).toFixed(1)}" class="st-pk__axle"/>`;
    const rm = r * 0.87, span = marks === 3 ? 62 : 74;
    for (let i = 0; i < marks; i++) {
      const t = mid + ANGLES[i];
      if (big) {
        const id = `stpk${++uid}`;
        s += `<path id="${id}" d="${arcPath(cx, cy, rm, t, span)}" class="st-pk__guide"/>` +
          `<text class="st-pk__txt"><textPath href="#${id}" startOffset="50%" text-anchor="middle" dominant-baseline="central">${esc(sample)}</textPath></text>`;
        const [lx, ly] = pt(cx, cy, r + 9, t);
        s += `<g class="st-pk__badge"><circle cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="5.6"/><text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" dominant-baseline="central" text-anchor="middle">${i + 1}</text></g>`;
      } else {
        s += `<path d="${arcPath(cx, cy, rm, t, span)}" class="st-pk__mark"/>`;
      }
    }
    s += `<text x="${cx}" y="${cy + r + (big ? 22 : 15)}" text-anchor="middle" class="st-pk__lbl">${label}</text></g>`;
    return s;
  }
  function svg(code, {big = false, sample = 'YOUR MARK', mid = 110} = {}) {
    const p = PACKAGES[code];
    if (!p) return '';
    const H = big ? 128 : 112;
    let s = `<svg class="st-pk__svg" viewBox="0 0 200 ${H}" aria-hidden="true" focusable="false">`;
    if (code === 'FRAME') {
      s += `<g class="st-pk__face is-blank"><circle cx="54" cy="52" r="30" class="st-pk__rim"/><circle cx="54" cy="52" r="8" class="st-pk__hub"/><text x="54" y="${big ? 104 : 97}" text-anchor="middle" class="st-pk__lbl">NO WHEEL MARK</text></g>` +
        `<rect x="104" y="40" width="84" height="22" rx="3" class="st-pk__frame"/>` +
        (big ? `<text x="146" y="51" text-anchor="middle" dominant-baseline="central" class="st-pk__txt st-pk__txt--frame">${esc(sample)}</text>`
          : `<rect x="114" y="47" width="64" height="8" rx="2" class="st-pk__mark-fill"/>`) +
        `<text x="146" y="${big ? 104 : 97}" text-anchor="middle" class="st-pk__lbl">FRAME, ONE SIDE</text>`;
    } else {
      const fr = p.frame > 0, r = fr ? 32 : 38, y = big ? 54 : 50;
      const fx = fr ? [40, 116] : [52, 148];
      s += face(fx[0], y, r, p.per, mid, 'FRONT FACE', big, sample);
      s += face(fx[1], y, r, p.wheel === 2 ? p.per : 0, mid, p.wheel === 2 ? 'BACK FACE' : 'BACK: BLANK', big, sample);
      if (fr) {
        s += `<rect x="168" y="${y - 38}" width="22" height="76" rx="3" class="st-pk__frame"/>` +
          (big ? `<text x="179" y="${y}" text-anchor="middle" dominant-baseline="central" transform="rotate(90 179 ${y})" class="st-pk__txt st-pk__txt--frame">${esc(sample.slice(0, 12))}</text>`
            : `<rect x="175" y="${y - 26}" width="8" height="52" rx="2" class="st-pk__mark-fill"/>`) +
          `<text x="179" y="${y + r + (big ? 22 : 15)}" text-anchor="middle" class="st-pk__lbl">FRAME</text>`;
      }
    }
    return s + '</svg>';
  }

  function surfaceSvg(k) {
    const wheel = (cx, on) => `<circle cx="${cx}" cy="30" r="20" class="st-sf__rim${on ? ' is-on' : ''}"/><circle cx="${cx}" cy="30" r="6" class="st-sf__hub"/>` +
      (on ? `<path d="M${cx - 13} 16.5A19 19 0 0 1 ${cx + 13} 16.5" class="st-sf__mark"/>` : '');
    const frame = on => `<rect x="62" y="6" width="12" height="48" rx="2" class="st-sf__frame${on ? ' is-on' : ''}"/>` +
      (on ? '<rect x="66" y="13" width="4" height="34" rx="1.5" class="st-sf__mark-fill"/>' : '');
    return `<svg class="st-sf__svg" viewBox="0 0 84 60" aria-hidden="true" focusable="false">${wheel(32, k !== 'frame')}${frame(k !== 'wheel')}</svg>`;
  }
  function currentSurface(root) {
    const st = state(root), m = model(root), cur = selected(root);
    if (st.surface && surfacesFor(m).includes(st.surface)) return st.surface;
    if (cur && !standalone(root)) return surfaceOf(cur.code);
    return '';
  }
  function renderSurfaces(root, m, kind, cur, active) {
    const wrap = q(root, '[data-eng-surfaces]');
    if (!wrap) return;
    const surf = currentSurface(root);
    const avail = surfacesFor(m);
    wrap.innerHTML = SURFACE_ORDER.map(k => {
      const sf = SURFACES[k];
      const opts = surfaceCodes(m, k).map(c => find(root, kind, c)).filter(Boolean);
      const ok = active && opts.length > 0;
      const why = !avail.includes(k) ? (m ? 'Not offered on this model' : 'Choose your tool first')
        : !active ? 'Choose what to engrave' : opts.length ? `from ${money(root, Math.min(...opts.map(o => Number(o.dataset.price))))}` : 'Unavailable';
      return `<button type="button" class="st-sf" data-eng-surface="${k}" aria-pressed="${surf === k}"${ok ? '' : ' disabled'}>` +
        `<span class="st-sf__art">${surfaceSvg(k)}</span><span class="st-sf__name">${esc(sf.name)}</span>` +
        `<span class="st-sf__short">${esc(sf.short)}</span><span class="st-sf__from">${esc(why)}</span></button>`;
    }).join('');
    const detail = q(root, '[data-eng-detail]');
    if (detail) detail.hidden = !surf || !active;
    const hint = q(root, '[data-eng-surface-hint]');
    if (hint) hint.textContent = !active ? '' : surf ? '' : 'Pick a surface to see its placement options.';
    void cur;
  }

  function markMid(root) {
    const cal = window.stSheavePreview?.calibration?.[model(root)];
    return cal ? cal.mid : 110;
  }
  function sample(root, kind) {
    if (kind === 'logo') return 'LOGO';
    const t = (q(root, '[data-eng-company]')?.value || '').trim();
    return t ? (t.length > 18 ? t.slice(0, 17) + '\u2026' : t) : 'YOUR COMPANY';
  }

  // ---------- Grid UI ----------
  function currentKind(root) {
    const sel = selected(root);
    return sel?.kind || state(root).kind || 'text';
  }
  function renderPanel(root) {
    const panel = q(root, '[data-eng-pkg-preview]');
    if (!panel) return;
    const st = state(root), sel = selected(root);
    const kind = currentKind(root);
    const code = st.hover || sel?.code || '';
    const m = model(root);
    if (!code || !PACKAGES[code] || !allowed(m).includes(code) || (currentSurface(root) && surfaceOf(code) !== currentSurface(root))) {
      panel.innerHTML = `<p class="st-pk__panel-empty">${m ? 'Choose a package to see where every mark goes.' : 'Choose your tool first.'}</p>`;
      panel.dataset.mode = 'empty';
      return;
    }
    const p = PACKAGES[code];
    const o = find(root, kind, code);
    const font = q(root, '[data-eng-font]')?.value || 'sans';
    panel.dataset.mode = st.hover && st.hover !== sel?.code ? 'preview' : 'selected';
    panel.dataset.font = font;
    const tag = panel.dataset.mode === 'preview' ? 'Previewing' : sel ? 'Selected' : 'Example';
    const angles = p.per === 3 ? ' Marks 1, 2 and 3 sit at 0\u00b0, 120\u00b0 and 240\u00b0.' : '';
    panel.innerHTML = `<div class="st-pk__panel-art">${svg(code, {big: true, sample: sample(root, kind), mid: markMid(root)})}</div>` +
      `<div class="st-pk__panel-copy"><span class="st-pk__tag">${tag}</span><strong>${esc(p.name)}</strong>` +
      `<span>${esc(p.desc)}${angles}</span>` +
      `<span class="st-pk__panel-meta">${p.total} mark${p.total === 1 ? '' : 's'} per tool \u00b7 ${o ? `${money(root, Number(o.dataset.price))} per tool` : 'Unavailable'}</span></div>`;
  }
  function render(root) {
    const sel = select(root);
    if (!sel) return;
    bind(root);
    relabel(root);
    const st = state(root), m = model(root), cur = selected(root);
    const none = sel.selectedOptions[0]?.dataset.none === 'true';
    if (cur) st.kind = cur.kind;
    if (st.surface && !surfacesFor(m).includes(st.surface)) st.surface = '';
    if (cur && st.surface && surfaceOf(cur.code) !== st.surface) st.surface = surfaceOf(cur.code);
    const kind = currentKind(root);
    root.querySelectorAll('[data-eng-model-btn]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.engModelBtn === m)));
    root.querySelectorAll('[data-eng-kind-btn]').forEach(b => {
      const k = b.dataset.engKindBtn;
      const on = k === 'none' ? !cur && !st.kind && !standalone(root) : cur ? cur.kind === k : st.kind === k;
      b.setAttribute('aria-pressed', String(on));
      if (k !== 'none') {
        const any = allowed(m).some(code => find(root, k, code));
        b.disabled = !any;
        const price = allowed(m).map(code => find(root, k, code)).filter(Boolean).map(o => Number(o.dataset.price));
        const from = b.querySelector('[data-eng-kind-from]');
        if (from) from.textContent = any ? `from ${money(root, Math.min(...price))}` : 'Unavailable';
      }
    });
    void none;
    const active = !!(cur || st.kind || standalone(root));
    renderSurfaces(root, m, kind, cur, active);
    const surf = currentSurface(root);
    const codes = surf ? surfaceCodes(m, surf) : [];
    const font = q(root, '[data-eng-font]')?.value || 'sans';
    root.querySelectorAll('[data-eng-font-btn]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.engFontBtn === font)));
    const grid = q(root, '[data-eng-packages]');
    if (grid) {
      const sig = `${m}:${kind}:${surf}:${[...sel.options].map(o => o.value + o.dataset.price + o.disabled).join(',')}`;
      if (sig !== st.sig) {
        st.sig = sig;
        const mid = markMid(root);
        grid.innerHTML = codes.map(code => {
          const p = PACKAGES[code], o = find(root, kind, code);
          const on = cur?.code === code;
          return `<button type="button" class="st-pk" data-eng-pkg="${code}" aria-pressed="${on}"${o ? '' : ' disabled'}>` +
            `<span class="st-pk__art">${svg(code, {mid})}</span>` +
            `<span class="st-pk__body"><span class="st-pk__name">${esc(p.name)}</span><span class="st-pk__short">${esc(p.short)}</span>` +
            `<span class="st-pk__price">${o ? money(root, Number(o.dataset.price)) : 'Unavailable'}<small>${o ? ' per tool' : ''}</small></span></span>` +
            `<span class="st-pk__check" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>`;
        }).join('');
        if (!codes.length) grid.innerHTML = `<p class="st-pk__panel-empty">${m ? 'Choose a surface to see its packages.' : 'Choose your tool to see its engraving packages.'}</p>`;
      } else {
        grid.querySelectorAll('[data-eng-pkg]').forEach(b => b.setAttribute('aria-pressed', String(cur?.code === b.dataset.engPkg)));
      }
      const label = q(root, '[data-eng-pkg-kind]');
      if (label) label.textContent = kind === 'logo' ? 'Prices shown for logo artwork' : 'Prices shown for company text';
    }
    renderPanel(root);
  }
  function commit(root, kind, code) {
    const o = find(root, kind, code);
    const err = q(root, '[data-eng-pkg-msg]');
    if (!o) {
      if (err) { err.textContent = 'That package is not available right now. Choose another option.'; err.hidden = false; }
      return;
    }
    if (err) err.hidden = true;
    state(root).kind = kind;
    state(root).surface = surfaceOf(code);
    state(root).hover = '';
    const sel = select(root);
    if (sel.selectedOptions[0] === o) { render(root); return; }
    o.selected = true;
    sel.dispatchEvent(new Event('change', {bubbles: true}));
  }
  function bind(root) {
    const st = state(root);
    if (st.bound) return;
    st.bound = true;
    root.addEventListener('click', e => {
      const t = e.target.closest?.('[data-eng-model-btn],[data-eng-kind-btn],[data-eng-surface],[data-eng-pkg],[data-eng-font-btn]');
      if (!t || !root.contains(t) || t.disabled) return;
      e.preventDefault();
      if (t.matches('[data-eng-model-btn]')) {
        const s = q(root, '[data-eng-sheave]');
        if (s && s.value !== t.dataset.engModelBtn) { s.value = t.dataset.engModelBtn; s.dispatchEvent(new Event('change', {bubbles: true})); }
      } else if (t.matches('[data-eng-kind-btn]')) {
        const k = t.dataset.engKindBtn, sel = select(root);
        if (k === 'none') {
          st.kind = ''; st.surface = ''; st.hover = '';
          const none = [...sel.options].find(o => o.dataset.none === 'true');
          if (none && !none.selected) { none.selected = true; sel.dispatchEvent(new Event('change', {bubbles: true})); }
          else render(root);
          return;
        }
        // Switching text/logo keeps the same package when valid; otherwise the first valid
        // package on the chosen surface. With no surface chosen yet, nothing is purchased.
        const cur = selected(root);
        st.kind = k;
        const surf = currentSurface(root);
        const code = cur && find(root, k, cur.code) ? cur.code
          : surf ? surfaceCodes(model(root), surf).find(c => find(root, k, c)) : '';
        if (code) commit(root, k, code);
        else if (cur && !standalone(root)) {
          st.surface = '';
          const none = [...sel.options].find(o => o.dataset.none === 'true');
          if (none) { none.selected = true; sel.dispatchEvent(new Event('change', {bubbles: true})); }
          st.kind = k; render(root);
        } else render(root);
      } else if (t.matches('[data-eng-surface]')) {
        // Changing the surface always commits a valid matching package (consent resets on change).
        const k = t.dataset.engSurface, kind = currentKind(root), cur = selected(root);
        if (cur && surfaceOf(cur.code) === k && cur.kind === kind) { st.surface = k; render(root); return; }
        const code = surfaceCodes(model(root), k).find(c => find(root, kind, c));
        if (code) commit(root, kind, code);
        else { const err = q(root, '[data-eng-pkg-msg]'); if (err) { err.textContent = 'That surface is not available right now. Choose another option.'; err.hidden = false; } }
      } else if (t.matches('[data-eng-pkg]')) {
        commit(root, currentKind(root), t.dataset.engPkg);
      } else if (t.matches('[data-eng-font-btn]')) {
        const f = q(root, '[data-eng-font]');
        if (f && f.value !== t.dataset.engFontBtn) {
          f.value = t.dataset.engFontBtn;
          f.dispatchEvent(new Event('input', {bubbles: true}));
          f.dispatchEvent(new Event('change', {bubbles: true}));
        }
        render(root);
      }
    });
    // Preview only: hover (mouse/pen) and keyboard focus never commit a purchase choice.
    const show = code => { if (st.hover !== code) { st.hover = code; renderPanel(root); } };
    root.addEventListener('pointerover', e => {
      if (e.pointerType === 'touch') return;
      const b = e.target.closest?.('[data-eng-pkg]');
      if (b && !b.disabled) show(b.dataset.engPkg);
    });
    root.addEventListener('pointerout', e => {
      const b = e.target.closest?.('[data-eng-pkg]');
      if (b && !b.contains(e.relatedTarget) && st.hover === b.dataset.engPkg && document.activeElement !== b) show('');
    });
    root.addEventListener('focusin', e => {
      const b = e.target.closest?.('[data-eng-pkg]');
      let kb = true;
      try { kb = b?.matches(':focus-visible'); } catch { /* older browsers */ }
      if (b && kb) show(b.dataset.engPkg);
    });
    root.addEventListener('focusout', e => {
      if (e.target.closest?.('[data-eng-pkg]')) show('');
    });
    root.addEventListener('input', e => {
      if (e.target.matches('[data-eng-company]')) {
        renderPanel(root);
        const t = e.target.value.trim();
        root.querySelectorAll('[data-eng-font-sample]').forEach(s => { s.textContent = t ? t.slice(0, 24) : 'Staten Tools'; });
      }
    });
  }

  // ---------- Exported front/back package diagram ----------
  const dstates = new WeakMap();
  function ensureDiagram(root) {
    let d = dstates.get(root);
    if (d && d.el.isConnected) return d;
    const el = document.createElement('div');
    el.className = 'st-pkd';
    el.dataset.stPkgDiagram = '';
    el.hidden = true;
    el.innerHTML = '<div class="st-pkd__head"><span class="st-pkd__title">Package layout \u2014 front and back faces</span><span class="st-pkd__count" data-pkd-count></span></div>' +
      '<div class="st-pkd__stage"><canvas class="st-pkd__canvas" role="img" aria-label="Package layout diagram"></canvas></div>' +
      '<button class="st-sheave__expand" type="button" data-pkd-expand>Expand diagram</button>' +
      '<p class="st-pkd__note" data-pkd-note>Illustrative diagram, not to scale. The product photo shows the front face only; the back face is drawn, not photographed.</p>';
    const host = q(root, '[data-eng-preview]');
    const after = host?.querySelector('[data-st-sheave-preview]');
    if (after) after.after(el); else if (host) host.append(el); else root.append(el);
    d = {el, canvas: el.querySelector('canvas'), ready: false, code: ''};
    Object.assign(d.canvas.style, {display: 'block', width: '100%', height: 'auto', maxWidth: '100%'});
    el.querySelector('[data-pkd-expand]').addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      window.stEngravingExpand?.open(d.canvas);
    });
    dstates.set(root, d);
    return d;
  }
  function arcArt(ctx, cx, cy, r0, r1, midDeg, spanDeg, art) {
    const rm = (r0 + r1) / 2, arc = rm * spanDeg * Math.PI / 180;
    const th = Math.min(r1 - r0, arc * art.height / art.width);
    const N = Math.max(30, Math.ceil(spanDeg * 1.6)), sw = art.width / N, dw = arc / N * 1.03;
    for (let i = 0; i < N; i++) {
      const u = (i + 0.5) / N, t = (midDeg + (0.5 - u) * spanDeg) * Math.PI / 180;
      ctx.save();
      ctx.translate(cx + rm * Math.cos(t), cy + rm * Math.sin(t));
      ctx.rotate(t - Math.PI / 2);
      ctx.drawImage(art, i * sw, 0, sw, art.height, -dw / 2, -th / 2, dw, th);
      ctx.restore();
    }
  }
  function label(ctx, txt, x, y, size, color, align = 'center', weight = 700) {
    ctx.font = `${weight} ${size}px Arial, sans-serif`;
    ctx.fillStyle = color; ctx.textAlign = align; ctx.textBaseline = 'middle';
    ctx.fillText(txt, x, y);
  }
  function drawFace(ctx, cx, cy, R, marks, mid, span, art, ready, title, sub) {
    ctx.save();
    ctx.globalAlpha = marks ? 1 : 0.45;
    ctx.fillStyle = '#c9cfc6'; ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = '#2a302b'; ctx.stroke();
    ctx.fillStyle = '#e4e8e1'; ctx.beginPath(); ctx.arc(cx, cy, R * 0.74, 0, Math.PI * 2); ctx.fill(); ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#b4bbb1'; ctx.beginPath(); ctx.arc(cx, cy, R * 0.24, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#2a302b'; ctx.beginPath(); ctx.arc(cx, cy, R * 0.08, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    const r0 = R * 0.77, r1 = R * 0.97;
    for (let i = 0; i < marks; i++) {
      const t = mid + ANGLES[i];
      if (ready && art) arcArt(ctx, cx, cy, r0, r1, t, span, art);
      else {
        ctx.save(); ctx.strokeStyle = '#4f8f22'; ctx.globalAlpha = 0.55; ctx.lineWidth = (r1 - r0) * 0.7; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.arc(cx, cy, (r0 + r1) / 2, (t - span / 2) * Math.PI / 180, (t + span / 2) * Math.PI / 180); ctx.stroke(); ctx.restore();
      }
      const [bx, by] = pt(cx, cy, R + 34, t);
      ctx.fillStyle = '#17221b'; ctx.beginPath(); ctx.arc(bx, by, 19, 0, Math.PI * 2); ctx.fill();
      label(ctx, String(i + 1), bx, by + 1, 21, '#fff');
      if (marks === 3) {
        const [ax, ay] = pt(cx, cy, R + 68, t);
        label(ctx, `${ANGLES[i]}\u00b0`, ax, ay, 19, '#376b29');
      }
    }
    label(ctx, title, cx, cy + R + 104, 26, '#17221b');
    label(ctx, sub, cx, cy + R + 136, 19, '#536157', 'center', 400);
  }
  function drawFrame(ctx, x, y, w, h, art, ready, vertical, title, sub) {
    ctx.fillStyle = '#20251f'; ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#0d110e'; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h);
    const pad = 14, iw = w - pad * 2, ih = h - pad * 2;
    if (ready && art) {
      ctx.save();
      ctx.translate(x + w / 2, y + h / 2);
      if (vertical) ctx.rotate(Math.PI / 2);
      const bw = vertical ? ih : iw, bh = vertical ? iw : ih;
      const s = Math.min(bw / art.width, bh / art.height);
      // Engraved mark reads light on the dark painted frame in this diagram.
      const tint = document.createElement('canvas'); tint.width = art.width; tint.height = art.height;
      const tc = tint.getContext('2d'); tc.drawImage(art, 0, 0); tc.globalCompositeOperation = 'source-in'; tc.fillStyle = '#d8e6cf'; tc.fillRect(0, 0, art.width, art.height);
      ctx.drawImage(tint, -art.width * s / 2, -art.height * s / 2, art.width * s, art.height * s);
      ctx.restore();
    } else {
      ctx.fillStyle = '#4f8f22'; ctx.globalAlpha = 0.6; ctx.fillRect(x + pad, y + pad, iw, ih); ctx.globalAlpha = 1;
    }
    label(ctx, title, x + w / 2, y + h + 40, 24, '#17221b');
    label(ctx, sub, x + w / 2, y + h + 70, 18, '#536157', 'center', 400);
  }
  function renderDiagram(root, art, opts = {}) {
    const d = ensureDiagram(root);
    const sel = selected(root);
    const m = model(root);
    d.ready = false; d.code = sel?.code || '';
    if (!sel || !m) { d.el.hidden = true; return; }
    d.el.hidden = false;
    const p = PACKAGES[sel.code];
    const ready = !!(opts.ready && art && art.width && art.height);
    const W = 1600, H = 900, cv = d.canvas;
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#f3f6f0'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#376b29'; ctx.fillRect(48, 40, 8, 58);
    label(ctx, `${p.name.toUpperCase()} PACKAGE \u2014 ${p.total} MARK${p.total === 1 ? '' : 'S'} PER TOOL`, 74, 56, 30, '#17221b', 'left');
    label(ctx, p.desc, 74, 90, 20, '#536157', 'left', 400);
    const cal = window.stSheavePreview?.calibration?.[m];
    const mid = cal ? cal.mid : 110;
    const wf = cal ? Math.min(1, (Number(opts.widthIn) || cal.maxW) / cal.maxW) : 1;
    if (sel.code === 'FRAME') {
      drawFrame(ctx, 380, 360, 840, 150, art, ready, false, 'SHEAVE FRAME \u2014 ONE SIDE ONLY', 'Fitted to the frame; final dimensions confirmed by our team. No wheel marks.');
    } else {
      const fr = p.frame > 0, R = fr ? 215 : 245, cy = 440;
      const span = Math.max(20, (p.per === 3 ? 72 : 82) * wf);
      const xs = fr ? [330, 900] : [420, 1180];
      drawFace(ctx, xs[0], cy, R, p.per, mid, span, art, ready, 'FRONT FACE', 'Photographed side');
      drawFace(ctx, xs[1], cy, R, p.wheel === 2 ? p.per : 0, mid, span, art, ready,
        p.wheel === 2 ? 'BACK FACE' : 'BACK FACE \u2014 NO MARKS', 'Reverse side, drawn as viewed facing it');
      if (fr) drawFrame(ctx, 1310, 190, 150, 500, art, ready, true, 'FRAME', 'One side, fitted to arm');
    }
    label(ctx, 'Illustrative layout, not to scale. Mark 1 matches the photo position; positions run clockwise as viewed facing that side.', W / 2, H - 34, 19, '#536157', 'center', 400);
    cv.setAttribute('aria-label', `Package diagram: ${p.name}. ${p.desc}`);
    q(d.el, '[data-pkd-count]').textContent = `${p.total} mark${p.total === 1 ? '' : 's'} per tool`;
    d.ready = ready;
  }
  async function exportDiagram(root) {
    const d = dstates.get(root);
    if (!d || d.el.hidden || !d.ready) throw Error('The package layout diagram is not ready. Check your text or artwork and try again.');
    const blob = await new Promise((res, rej) => d.canvas.toBlob(b => b ? res(b) : rej(Error('Could not save the package diagram.')), 'image/png'));
    return new File([blob], `engraving-package-${d.code.toLowerCase()}.png`, {type: 'image/png'});
  }

  window.stEngravingPackages = {PACKAGES, EXPECTED, HOOK, FEED, SURFACES, SURFACE_ORDER, surfaceOf, surfaceCodes, surfacesFor, ANGLES, model, allowed, usable, selected, reconcile, render,
    renderDiagram, exportDiagram, fontLabel, money};
})();
